import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Configuration, FrontendApi, LoginFlow } from '@ory/client';
import Config from 'src/common/Config';
import { Button, TextInput } from 'src/components';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { Alert, Center, Divider, Stack, Flex } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { OidcSection } from 'src/components/convenience/auth/OidcSection';
import { OIDC_ATTEMPTED_PROVIDER_KEY } from 'src/components/convenience/auth/OidcFormButton';
import { TermsAndPrivacy } from 'src/auth/components/TermsAndPrivacy';
import { isOryApiError, getCsrfTokenFromFlow } from 'src/auth/utils/ory-helpers';
import { clearInviteData } from 'src/auth/utils/inviteStorage';
import { z } from 'zod';
import { useIsMobile } from 'src/common/hooks/useIsMobile';
import { Logger } from 'src/utils/Logger';
import { useFlags } from 'launchdarkly-react-client-sdk';

const ory = new FrontendApi(
  new Configuration({
    basePath: Config.getAuthBasePath(),
    baseOptions: {
      withCredentials: true, // important for cookies
    },
  }),
);

const emailSchema = z.string().email();

type Props = {
  flow: LoginFlow;
  setFlow: (flow: LoginFlow) => void;
  setError: (error: string | undefined) => void;
  resetFlow: () => Promise<unknown>;
  initialEmail?: string | null;
};

export default function LoginTabContent({
  flow,
  setFlow,
  setError,
  resetFlow,
  initialEmail,
}: Props) {
  const { isMobile } = useIsMobile();
  const { flagEnabledMobileLoginFallback } = useFlags();
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [isRestartingFlow, setIsRestartingFlow] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEmailValid = emailSchema.safeParse(email).success;

  const emailNode = useMemo(() => {
    return flow.ui.nodes.find(node => {
      return (
        node.group === 'identifier_first' &&
        node.meta?.label?.context &&
        'name' in node.meta.label.context &&
        node.meta?.label?.context.name === 'traits.email'
      );
    });
  }, [flow]);

  useEffect(() => {
    if (
      !initialEmail &&
      emailNode &&
      'value' in emailNode.attributes &&
      emailNode.attributes.value
    ) {
      setEmail(String(emailNode.attributes.value));
    }
  }, [emailNode, initialEmail]);

  const submitLoginFlow = async (flowId: string, values: Record<string, unknown>) => {
    const { data } = await ory.updateLoginFlow({
      flow: flowId,
      updateLoginFlowBody: {
        method: 'password',
        identifier: values.identifier as string,
        password: values.password as string,
        csrf_token: values.csrf_token as string,
      },
    });

    if (data.session) {
      clearInviteData();
      window.location.href = '/';
      return true;
    }

    if ('ui' in data) {
      setFlow(data as unknown as LoginFlow);
    }

    return false;
  };

  const handleSubmitError = (e: unknown) => {
    const response = isOryApiError(e) ? e.response : undefined;
    const data = response?.data;

    if (data && typeof data === 'object') {
      if (response?.status === 400 && 'ui' in data && data.ui) {
        setFlow(data as LoginFlow);
        setError(undefined);
        return false;
      }

      if (
        response?.status === 422 &&
        'redirect_browser_to' in data &&
        typeof data.redirect_browser_to === 'string'
      ) {
        window.location.href = data.redirect_browser_to;
        return true;
      }
    }

    notifyAndLogError('Login error', 'An error occurred during login.', e);
    setError('Login failed. Please try again.');
    return false;
  };

  const loggedBrokenFlowId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (emailNode) return;
    if (loggedBrokenFlowId.current === flow.id) return;
    loggedBrokenFlowId.current = flow.id;
    Logger.error(
      'Login flow has no email input node',
      new Error('Login flow has no email input node'),
      {
        flowId: flow.id,
        isRefreshFlow: flow.refresh,
        hasOauthLoginChallenge: !!flow.oauth2_login_challenge,
        nodes: JSON.stringify(flow.ui.nodes),
      },
    );
  }, [emailNode, flow]);

  const restartFlow = async () => {
    setIsRestartingFlow(true);
    // A stale Ory session is the known cause of flows we can't render, so log it
    // out before restarting to get a fresh identifier-first flow
    try {
      const { data: logoutFlow } = await ory.createBrowserLogoutFlow();
      await ory.updateLogoutFlow({ token: logoutFlow.logout_token });
    } catch (e) {
      // A failed logout here likely means the user is stuck: they can't log out
      // but the flow has no nodes to render either
      Logger.error('Failed to log out before restarting the login flow', e, {
        flowId: flow.id,
      });
    }

    const oauthRequestUrl = flow.oauth2_login_request?.request_url;
    if (oauthRequestUrl) {
      window.location.href = oauthRequestUrl;
      return;
    }

    await resetFlow();
    setIsRestartingFlow(false);
  };

  // The success paths start a full page navigation, so the button holds its
  // loading state there rather than flicking back to idle before the page changes
  const finishSubmitting = (isNavigatingAway: boolean) => {
    if (!isNavigatingAway) {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const values: Record<string, unknown> = {};
    formData.forEach((value, key) => {
      values[key] = value.toString();
    });

    setIsSubmitting(true);

    try {
      finishSubmitting(await submitLoginFlow(flow.id, values));
    } catch (e: unknown) {
      // A login flow expires after a period of inactivity (e.g. when a session
      // times out and the user is left on a stale login page). Submitting
      // against it returns 410 Gone. Rather than dead-ending the user, create a
      // fresh flow and resubmit with its CSRF token so they can continue.
      if (isOryApiError(e) && e.response?.status === 410) {
        try {
          const { data: newFlow } = await ory.createBrowserLoginFlow({
            loginChallenge: flow.oauth2_login_challenge,
          });
          finishSubmitting(
            await submitLoginFlow(newFlow.id, {
              ...values,
              csrf_token: getCsrfTokenFromFlow(newFlow),
            }),
          );
        } catch (retryError: unknown) {
          finishSubmitting(handleSubmitError(retryError));
        }
        return;
      }

      finishSubmitting(handleSubmitError(e));
    }
  };

  // needed for hidden fields like csrf_token
  const hiddenNodes = flow.ui.nodes
    .filter(node => 'type' in node.attributes && node.attributes.type === 'hidden')
    .map(node => {
      const attributes = node.attributes as unknown as Record<string, string>;
      return (
        <input
          key={`${attributes.name}-${attributes.type}`}
          name={attributes.name || ''}
          type={attributes.type}
          defaultValue={attributes.value}
          required={!!attributes.required}
          disabled={!!attributes.disabled}
        />
      );
    });

  const passwordNode = flow.ui.nodes.find(node => {
    return (
      node.group === 'default' && 'name' in node.attributes && node.attributes.name === 'password'
    );
  });

  // this with the key in the email text input is a bit of a hack to force re-mounting
  // and preventing the email field to be highlighted in yellow by Dashlane after pressing continue
  const getEmailValue = () => {
    if (emailNode && 'value' in emailNode.attributes && emailNode.attributes.value) {
      return emailNode.attributes.value;
    }
    if (initialEmail) {
      return initialEmail;
    }
    return undefined;
  };
  const emailValue = getEmailValue();
  const emailValueProps = emailValue ? { value: emailValue } : {};

  const [attemptedProvider, setAttemptedProvider] = useState<string | null>(null);

  useEffect(() => {
    const storedProvider = localStorage.getItem(OIDC_ATTEMPTED_PROVIDER_KEY);
    if (storedProvider) {
      localStorage.removeItem(OIDC_ATTEMPTED_PROVIDER_KEY);
      setAttemptedProvider(storedProvider);
    }
  }, []);

  if (!emailNode && flagEnabledMobileLoginFallback) {
    return (
      <Stack gap="lg" mt="xl">
        <Alert color="red" variant="light" icon={<IconAlertCircle />} radius="md">
          Something went wrong loading the log in form. Please try again.
        </Alert>
        <Button fullWidth loading={isRestartingFlow} onClick={restartFlow}>
          Try again
        </Button>
      </Stack>
    );
  }

  const errorMessages = flow.ui.messages?.filter(m => m.type === 'error') ?? [];

  const registrationDisabledError = errorMessages.find(
    m => m.text.includes('Registration is not allowed') || m.id === 4000002,
  );

  const getErrorMessage = () => {
    if (registrationDisabledError) {
      return `The selected ${attemptedProvider ?? 'SSO'} account is not associated with a registered user. Please sign in using your username and password, or contact support for assistance.`;
    }
    if (errorMessages.length === 0) return null;
    if (errorMessages.length === 1) return errorMessages[0].text;
    return errorMessages.map(m => `• ${m.text}`).join('\n');
  };

  const errorMessage = getErrorMessage();

  const oidcNodes = flow.ui.nodes.filter(node => node.group === 'oidc');

  const csrfToken =
    (
      flow.ui.nodes.find(node => 'name' in node.attributes && node.attributes.name === 'csrf_token')
        ?.attributes as { value?: string }
    )?.value || '';

  return (
    <Stack gap="lg">
      {errorMessage && (
        <Alert color="red" variant="light" icon={<IconAlertCircle />} radius="md" mt="md" mb="-sm">
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        {emailNode && (
          <>
            <TextInput
              key={`email-${!!emailNode}-${!!passwordNode}`}
              name={'name' in emailNode.attributes ? emailNode.attributes.name : undefined}
              disabled={!!passwordNode}
              mt={oidcNodes.length > 0 ? 'md' : 'xl'}
              mb="md"
              required
              label="Email"
              placeholder={'name@example.com'}
              autoFocus={!initialEmail}
              onChange={e => setEmail(e.currentTarget.value)}
              type="email"
              {...emailValueProps}
            />
            {/*
              When the test input is disabled, it's value is not passed in as part of the submit
              action. To work around this, we add a hidden input with the same name and value.
            */}
            {passwordNode && 'value' in emailNode.attributes && (
              <input
                type="hidden"
                name={'name' in emailNode.attributes ? emailNode.attributes.name : undefined}
                value={emailNode.attributes.value}
              />
            )}
            {hiddenNodes}
          </>
        )}
        {emailNode && passwordNode && (
          <TextInput
            mt="xl"
            mb="md"
            required
            label="Password"
            placeholder={'Enter your password'}
            type={'password'}
            autoComplete="current-password"
            name={'name' in passwordNode.attributes ? passwordNode.attributes.name : undefined}
            autoFocus
            onChange={e => setPassword(e.currentTarget.value)}
          />
        )}
        <Button
          type={'submit'}
          fullWidth
          loading={isSubmitting}
          disabled={passwordNode ? password.trim() === '' : !isEmailValid}
        >
          {passwordNode ? 'Sign In' : 'Continue'}
        </Button>
        <Center mt="xxs">
          <Flex gap="xxxs" direction="row">
            {!isMobile && (
              <Button variant={'secondary'} to={'/recovery'} size={'xs'}>
                Forgot your password?
              </Button>
            )}
            {passwordNode && (
              <Button variant={'secondary'} onClick={resetFlow} size={'xs'}>
                Want to switch accounts?
              </Button>
            )}
          </Flex>
        </Center>
      </form>

      <Divider label="or" labelPosition="center" />

      <OidcSection nodes={oidcNodes} formAction={flow.ui.action} csrfToken={csrfToken} />

      <TermsAndPrivacy />
    </Stack>
  );
}
